import type maplibregl from 'maplibre-gl'

const LARGE_CAMERA_ZOOM_DELTA = 4
const EXPENSIVE_TERRAIN_LAYER_IDS = ['Hillshade', 'Contour index', 'Contour', 'Contour labels']

interface TerrainLayerRestoreState {
  timer: number | null
  moveEnd: (() => void) | null
  visibility: Map<string, string>
}

const terrainLayerState = new WeakMap<maplibregl.Map, TerrainLayerRestoreState>()

function getTerrainLayerRestoreState(map: maplibregl.Map): TerrainLayerRestoreState {
  const existing = terrainLayerState.get(map)
  if (existing) return existing

  const next = {
    timer: null,
    moveEnd: null,
    visibility: new Map<string, string>(),
  }
  terrainLayerState.set(map, next)
  return next
}

export function restoreExpensiveTerrainLayers(map: maplibregl.Map | null | undefined): void {
  if (!map) return

  const state = getTerrainLayerRestoreState(map)
  if (state.timer !== null) {
    window.clearTimeout(state.timer)
    state.timer = null
  }

  if (state.moveEnd) {
    map.off('moveend', state.moveEnd)
    state.moveEnd = null
  }

  state.visibility.forEach((visibility, layerId) => {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', visibility)
    }
  })
  state.visibility.clear()
}

function hideExpensiveTerrainLayersForMove(map: maplibregl.Map, durationMs: number): void {
  const state = getTerrainLayerRestoreState(map)
  if (state.moveEnd) {
    map.off('moveend', state.moveEnd)
    state.moveEnd = null
  }
  if (state.timer !== null) {
    window.clearTimeout(state.timer)
    state.timer = null
  }

  for (const layerId of EXPENSIVE_TERRAIN_LAYER_IDS) {
    if (!map.getLayer(layerId)) continue

    const visibility = map.getLayoutProperty(layerId, 'visibility') ?? 'visible'
    if (!state.visibility.has(layerId)) {
      state.visibility.set(layerId, visibility)
    }
    if (visibility !== 'none') {
      map.setLayoutProperty(layerId, 'visibility', 'none')
    }
  }

  if (state.visibility.size === 0) return

  const restore = () => restoreExpensiveTerrainLayers(map)
  state.moveEnd = restore
  map.once('moveend', restore)
  state.timer = window.setTimeout(restore, Math.max(durationMs + 500, 1000))
}

function prepareProgrammaticCameraMove(
  map: maplibregl.Map,
  targetZoom: number,
  durationMs: number,
): void {
  const zoomDelta = map.getZoom() - targetZoom
  const isLargeCameraMove = Math.abs(zoomDelta) >= LARGE_CAMERA_ZOOM_DELTA

  map.stop()

  if (isLargeCameraMove) {
    hideExpensiveTerrainLayersForMove(map, durationMs)
  } else {
    restoreExpensiveTerrainLayers(map)
  }
}

export function optimizeFitBoundsCameraOptions(
  map: maplibregl.Map,
  bounds: maplibregl.LngLatBounds,
  options: maplibregl.FitBoundsOptions,
): maplibregl.FitBoundsOptions {
  const nextOptions: maplibregl.FitBoundsOptions = { ...options }
  const targetCamera = map.cameraForBounds(bounds, nextOptions)
  const targetZoom = targetCamera?.zoom ?? map.getZoom()
  const zoomDelta = map.getZoom() - targetZoom
  const shouldPreserveExplicitAnimation =
    nextOptions.animate === true && Number(nextOptions.duration ?? 0) > 0

  if (zoomDelta >= LARGE_CAMERA_ZOOM_DELTA && !shouldPreserveExplicitAnimation) {
    nextOptions.duration = 0
    nextOptions.linear = true
  }

  prepareProgrammaticCameraMove(map, targetZoom, Number(nextOptions.duration ?? 0))
  return nextOptions
}

export function optimizeFlyToCameraOptions(
  map: maplibregl.Map,
  options: maplibregl.FlyToOptions,
): maplibregl.FlyToOptions {
  const nextOptions: maplibregl.FlyToOptions = { ...options }
  const targetZoom = nextOptions.zoom ?? map.getZoom()
  const zoomDelta = map.getZoom() - targetZoom

  if (zoomDelta >= LARGE_CAMERA_ZOOM_DELTA) {
    nextOptions.duration = 0
  }

  prepareProgrammaticCameraMove(map, targetZoom, Number(nextOptions.duration ?? 0))
  return nextOptions
}
